import {
    TableFilterButtonType, useDataGridActions,
    useGetTableFilterItems,
    useSetTableFilterItems,
} from "@/store/dataGrid.c";
import {useMemo} from "react";
import TextField from "@mui/material/TextField";
import {RemoveCircleOutline} from "@mui/icons-material";
import * as React from "react";
import {useAllDict} from "@/store/dict.c";
import {Controller, UseFormReturn} from "react-hook-form";
import {Autocomplete, Stack} from "@mui/material";
import isEmpty from "lodash/isEmpty";
import {useRoleUsers} from "@/store/user.c";
import {RoleUserVO} from "@/service/user.s";
import {RegionType, useRegions} from "@/store/region.c";
import {callDebounce, getLastStr} from "@/helper/method";
import OutlinedInput from "@mui/material/OutlinedInput";
import IconButton from "@mui/material/IconButton";
import {KeyboardCommandKeyOutlined, SearchRounded, CalendarToday, CloseOutlined} from "@mui/icons-material";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from 'react-datepicker';
import {roleAlias} from "@/helper/constant";
import dayjs from "dayjs";
import {DictPO} from "@/model/system";

interface SearchFormProps {
    code: string;
    onSearch: (params: any) => void;
    searchForm: UseFormReturn;
    showSearch: boolean;
}

const SearchForm: React.FC<SearchFormProps> = ({
                                                   code,
                                                   onSearch,
                                                   searchForm,
                                                   showSearch
                                               }) => {
    const tableFilterItems = useGetTableFilterItems(code);
    const setTableFilterItems = useSetTableFilterItems(code);
    const allDict = useAllDict();
    const sellerUsers = useRoleUsers([roleAlias["marketing"], roleAlias["manager"], roleAlias["bulletin"]]);
    const patenterUsers = useRoleUsers([roleAlias["patenter"], roleAlias["processer"]]);
    const declarerUsers = useRoleUsers([roleAlias["declarer"]]);
    const allUsers = useRoleUsers([])
    const {setSearchParam} = useDataGridActions()

    const {register, getValues, control, setValue, reset} = searchForm;
    const regions = useRegions();

    const onSubmit = async (values?: any) => {
        const params = {...getValues(), ...values};
        setSearchParam(code, params)
        onSearch && onSearch(params);
    };
    const renderRemoveIcon = (item: TableFilterButtonType, defaultNode?: React.ReactNode) => {
        return item.tag != 0 ? (
            <RemoveCircleOutline
                onClick={() =>
                    setTableFilterItems({...item, tag: -1})
                }
                cursor={"pointer"}
                fontSize={"small"}
                color={"action"}
            />
        ) : defaultNode || undefined
    }

    const renderUserSelect = (item: TableFilterButtonType, index: number, options: RoleUserVO[]) => {
        return <Autocomplete
            disablePortal
            key={index}
            {...register(item.attr)}
            onChange={(_, value: RoleUserVO | null) =>
                onSubmit({[item.attr]: value?.id?.toString()})
            }
            getOptionLabel={option => option.username}
            getOptionKey={option => option.id}
            options={options || []}
            sx={{
                width: 142,
            }}
            popupIcon={renderRemoveIcon(item)}
            renderInput={(params) => (
                <TextField sx={{"& .MuiInputBase-root ": {backgroundColor: 'transparent'}}} key={index} placeholder={item.title} {...params} />
            )}
        />
    }

    const renderForms = useMemo(() => {
            return tableFilterItems?.map(
                (item: TableFilterButtonType, index: number) => {
                    switch (item.type) {
                        case "dateRange":
                            return <DatePickers key={index}>
                                <Controller control={control} render={({field: {onChange, value}}) => {
                                    const [startDate, endDate] = value || []
                                    return <DatePicker
                                        showYearDropdown={true}
                                        selectsRange={true}
                                        showMonthDropdown={true}
                                        startDate={startDate}
                                        endDate={endDate}
                                        dateFormat="yyyy/MM/dd"
                                        id='account-settings-date'
                                        onChange={async (date: [Date | null, Date | null]) => {
                                            const [startDate, endDate] = date
                                            onChange(date)
                                            if (startDate && endDate) {
                                                const startDates = dayjs(startDate).format("YYYY-MM-DD")
                                                const endDates = dayjs(endDate).format("YYYY-MM-DD")
                                                setValue(item.attr, [startDates, endDates])
                                                await onSubmit({[item.attr]: [startDates, endDates]})
                                            }
                                        }}
                                        locale='zhCN'
                                        placeholderText={item.title}
                                        customInput={<TextField sx={{
                                            width: 180,
                                            "& .MuiInputBase-root": {padding: "8px 8px 8px 12px", backgroundColor: 'transparent'},
                                        }} slotProps={{
                                            input: {
                                                endAdornment: !isEmpty(value) ? <CloseOutlined onClick={async event => {
                                                    event.stopPropagation()
                                                    setValue(item.attr, [])
                                                    onChange([])
                                                    await onSubmit({[item.attr]: []})
                                                }} {...register(item.attr)} cursor={'pointer'} fontSize={'small'} color={'disabled'}/> : renderRemoveIcon(item, <CalendarToday
                                                    fontSize={'small'} color={'disabled'}/>)

                                            }
                                        }}
                                        />}
                                    />
                                }} name={`${item.type}${item.attr}`}/>
                            </DatePickers>
                        case "input":
                            return (
                                <TextField
                                    key={index}
                                    placeholder={item.title}
                                    type={'search'}
                                    {...register(item.attr)}
                                    onChange={event => callDebounce({[item.attr]: event.target.value}, onSubmit)}
                                    sx={{
                                        width: item.width || 128,
                                        "& .MuiInputBase-root": item.tag == 0 ? {padding: "8px 0px 8px 10px", backgroundColor: 'transparent'} : {
                                            backgroundColor: 'transparent',
                                            padding: "8px 8px 8px 10px"
                                        },
                                    }}
                                    slotProps={{
                                        input: {
                                            endAdornment: renderRemoveIcon(item, <InputAdornment position="start" sx={{color: "text.secondary"}}>
                                                <SearchRounded fontSize="small"/>
                                            </InputAdornment>),
                                        },
                                    }
                                    }
                                />);
                        case
                        "select":
                            if (item.key == 'seller') {
                                return renderUserSelect(item, index, sellerUsers)
                            } else if (item.key == 'patenter') {
                                return renderUserSelect(item, index, patenterUsers)
                            } else if (item.key == 'declarer') {
                                return renderUserSelect(item, index, declarerUsers)
                            } else if (item.key == 'user') {
                                return renderUserSelect(item, index, allUsers)
                            } else if (item.key == 'location') {
                                return <Autocomplete
                                    id="locationId"
                                    key={index}
                                    {...register(item.attr)}
                                    popupIcon={renderRemoveIcon(item)}
                                    groupBy={(option: RegionType) => option.group}
                                    getOptionLabel={(option: RegionType) => option.title}
                                    options={regions}
                                    onChange={(_, value: RegionType | null) =>
                                        onSubmit({[item.attr]: getLastStr(value?.value, '')})
                                    }
                                    sx={{
                                        width: 142,
                                        backgroundColor: 'transparent'
                                    }}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            placeholder={"所在区域"}
                                            sx={{"& .MuiInputBase-root ": {backgroundColor: 'transparent'}}}
                                            {...params}
                                        />
                                    )}
                                />
                            } else {
                                const items: DictPO[] = allDict[item.key!]! || [];
                                return (
                                    <Autocomplete
                                        disablePortal
                                        key={index}
                                        {...register(item.attr)}
                                        onChange={(_, value: any) =>
                                            onSubmit({[item.attr]: value?.value?.toString()})
                                        }
                                        defaultValue={item.defaultValue || null}
                                        popupIcon={renderRemoveIcon(item)}
                                        options={items}
                                        sx={{
                                            width: item.width || 142,
                                        }}
                                        renderInput={(params) => (
                                            <TextField sx={{"& .MuiInputBase-root ": {backgroundColor: 'transparent'}}} key={index} placeholder={item.title} {...params} />
                                        )}
                                    />
                                );
                            }
                    }
                },
            );
        },
        [tableFilterItems]);

    return <Stack
        useFlexGap
        rowGap={1}
        direction={"row"}
        sx={{flexWrap: "wrap"}}
        spacing={1.3}
    >{showSearch && <FormControl sx={{width: {xs: "100%", sm: '20ch', md: "22ch", lg: "24ch", xl: "30ch"},}} variant="outlined">
        <OutlinedInput
            size="small"
            id="keyword"
            {...register("keyword")}
            onChange={event => callDebounce({keyword: event.target.value}, onSubmit)}
            placeholder="搜索"
            enterKeyHint={"enter"}
            sx={{
                flexGrow: 1,
                padding: '8px 0px 8px 8px',
                backgroundColor: 'transparent',
                "& .MuiInputAdornment-root": {
                    marginRight: '4px',
                }
            }}
            endAdornment={
                <IconButton onClick={async () => {
                    await onSubmit({keyword: getValues("keyword")})
                }} size={'small'} sx={{marginRight: '3px'}}>
                    <KeyboardCommandKeyOutlined color="action" fontSize="small"/>
                </IconButton>
            }
            startAdornment={
                <InputAdornment position="start" sx={{color: "text.secondary"}}>
                    <SearchRounded fontSize="small"/>
                </InputAdornment>
            }
            inputProps={{
                type: "search",
                "aria-label": "search",
            }}
        />
    </FormControl>}{renderForms}</Stack>;
};

export default SearchForm;
